'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDeskToast } from './desk-feedback';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  Copy,
  Download,
  Eye,
  FilePlus2,
  History,
  Loader2,
  MessageCircle,
  PenLine,
  RefreshCw,
  Send,
  ShieldCheck,
  X,
  XCircle,
} from 'lucide-react';
import type { Booking } from '@/types';
import { api, ApiError } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types (loose on purpose: the contracts API is built in a parallel stream)
// ---------------------------------------------------------------------------

type ContractStatus =
  | 'draft'
  | 'sent'
  | 'viewed'
  | 'signing'
  | 'signed'
  | 'cancelled'
  | 'superseded';

interface ContractRow {
  id: string;
  number: string;
  version: number;
  status: ContractStatus | string;
  language?: string;
  createdAt: string;
  _count?: Record<string, number>;
  [key: string]: unknown;
}

interface SessionRow {
  id?: string;
  phoneMasked?: string;
  maskedPhone?: string;
  phone?: string;
  expiresAt?: string;
  createdAt?: string;
  [key: string]: unknown;
}

interface SignatureRow {
  id?: string;
  role?: string;
  signerName?: string;
  name?: string;
  signedAt?: string;
  createdAt?: string;
  [key: string]: unknown;
}

interface AuditEntry {
  id?: string;
  event?: string;
  action?: string;
  actor?: string;
  actorRole?: string;
  createdAt?: string;
  date?: string;
  [key: string]: unknown;
}

interface SnapshotShape {
  parties?: Record<string, unknown>;
  vehicle?: Record<string, unknown>;
  dates?: Record<string, unknown>;
  financials?: Record<string, unknown>;
  terms?: Array<unknown> | Record<string, unknown>;
  [key: string]: unknown;
}

interface ContractFull extends ContractRow {
  snapshot?: SnapshotShape | null;
  totalPrice?: number;
  depositAmount?: number;
  cautionAmount?: number;
  notes?: string;
  bookingId?: string;
}

interface DetailResponse {
  contract?: ContractFull;
  sessions?: SessionRow[];
  signatures?: SignatureRow[];
  audits?: AuditEntry[];
  // Fallback: flat shape where the body itself is the contract
  id?: string;
  snapshot?: SnapshotShape | null;
  [key: string]: unknown;
}

interface SendResult {
  link: string;
  expiresAt: string;
  waLink: string;
  maskedPhone: string;
}

const STATUS_CHIP: Record<string, string> = {
  draft: 'border-line bg-shell text-muted',
  sent: 'border-warning/30 bg-warning/10 text-warning',
  viewed: 'border-line bg-shell text-muted',
  signing: 'border-warning/30 bg-warning/10 text-warning',
  signed: 'border-success/30 bg-success/10 text-success',
  cancelled: 'border-danger/30 bg-danger/10 text-danger',
  superseded: 'border-line bg-shell text-muted',
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'Brouillon',
  sent: 'Envoyé',
  viewed: 'Vu',
  signing: 'Signature',
  signed: 'Signé',
  cancelled: 'Annulé',
  superseded: 'Remplacé',
};

const FLOW_STEPS = ['Brouillon', 'Envoyé', 'Vu', 'Signature', 'Signé'];

function statusToFlowIndex(status: string): number {
  switch (status) {
    case 'draft':
      return 0;
    case 'sent':
      return 1;
    case 'viewed':
      return 2;
    case 'signing':
      return 3;
    case 'signed':
      return 4;
    default:
      return -1;
  }
}

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function fmtMoney(v: unknown): string {
  if (typeof v !== 'number') return '—';
  return `${v.toLocaleString('fr-FR')} MAD`;
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : v == null ? '—' : String(v);
}

function surfaceError(err: unknown, notify: (message: string, tone?: 'ok' | 'error') => void, fallback: string): void {
  const detail = err instanceof ApiError && (err.status === 409 || err.status === 403)
    ? err.message
    : err instanceof Error
      ? err.message
      : 'Erreur inconnue';
  notify(`${fallback} : ${detail}`, 'error');
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export function ContractsPanel({ bookings }: { bookings: Booking[] }) {
  const notify = useDeskToast();
  const openBookings = useMemo(
    () => bookings.filter((b) => b.status === 'pending' || b.status === 'confirmed' || b.status === 'active'),
    [bookings]
  );

  const [selectedBookingId, setSelectedBookingId] = useState<string>('');
  const selectedBooking = useMemo(
    () => openBookings.find((b) => b.id === selectedBookingId) ?? null,
    [openBookings, selectedBookingId]
  );

  const [contracts, setContracts] = useState<ContractRow[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedContractId, setSelectedContractId] = useState<string>('');
  const [detail, setDetail] = useState<DetailResponse | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [busy, setBusy] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  // Send form
  const [sendPhone, setSendPhone] = useState('');
  const [expiresHours, setExpiresHours] = useState('48');
  const [sendResult, setSendResult] = useState<SendResult | null>(null);

  // Edit draft form
  const [editTotal, setEditTotal] = useState('');
  const [editAvance, setEditAvance] = useState('');
  const [editCaution, setEditCaution] = useState('');
  const [editNotes, setEditNotes] = useState('');

  // Cancel form
  const [cancelReason, setCancelReason] = useState('');

  const refreshList = useCallback(async (bookingId: string, silent = false) => {
    if (!bookingId) return;
    if (!silent) setLoadingList(true);
    setListError(null);
    try {
      const list = await api<ContractRow[]>(`/api/bookings/${bookingId}/contracts`);
      const sorted = [...(Array.isArray(list) ? list : [])].sort((a, b) => b.version - a.version);
      setContracts(sorted);
      // Auto-select latest if nothing selected or selection vanished
      setSelectedContractId((prev) => {
        if (prev && sorted.some((c) => c.id === prev)) return prev;
        return sorted[0]?.id ?? '';
      });
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Échec du chargement des contrats.');
      if (!silent) setContracts([]);
    } finally {
      if (!silent) setLoadingList(false);
    }
  }, []);

  const fetchDetail = useCallback(async (contractId: string) => {
    if (!contractId) {
      setDetail(null);
      return;
    }
    setLoadingDetail(true);
    try {
      const data = await api<DetailResponse>(`/api/contracts/${contractId}`);
      setDetail(data);
    } catch (err) {
      surfaceError(err, notify, 'Échec du chargement du détail');
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  // The client signs on their own phone. Keep the desk list fresh so the
  // signed contract appears without the agent having to guess when to refresh.
  useEffect(() => {
    if (!selectedBookingId) return;
    const refresh = () => {
      void refreshList(selectedBookingId, true);
    };
    const timer = window.setInterval(refresh, 15000);
    const onFocus = () => refresh();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [selectedBookingId, refreshList]);

  // Booking select: reset + load list (event-driven, no effects)
  function handleSelectBooking(bookingId: string) {
    setSelectedBookingId(bookingId);
    setSendResult(null);
    if (!bookingId) {
      setContracts([]);
      setSelectedContractId('');
      setDetail(null);
      return;
    }
    void refreshList(bookingId);
  }

  // Contract select: load detail (event-driven, no effects)
  function handleSelectContract(contractId: string) {
    setSelectedContractId(contractId);
    if (contractId) void fetchDetail(contractId);
    else setDetail(null);
  }

  function openSend() {
    if (selectedBooking) setSendPhone(selectedBooking.customerPhone ?? '');
    setSendResult(null);
    setShowSend(true);
  }

  function openEdit() {
    const c: ContractFull | undefined =
      detail?.contract ?? (detail?.id ? (detail as unknown as ContractFull) : undefined);
    const snap = (c?.snapshot ?? {}) as SnapshotShape;
    const fin = (snap.financials ?? {}) as Record<string, unknown>;
    const pick = (v: unknown, fb: unknown): string =>
      typeof v === 'number' ? String(v) : typeof fb === 'number' ? String(fb) : '';
    setEditTotal(pick(c?.totalPrice, fin.totalPrice ?? fin.total));
    setEditAvance(pick(c?.depositAmount, fin.avanceAmount ?? fin.avance ?? fin.deposit ?? fin.depositAmount));
    setEditCaution(pick(c?.cautionAmount, fin.caution ?? fin.deposit ?? fin.guarantee));
    setEditNotes(
      typeof c?.notes === 'string' ? (c.notes as string) : typeof snap.notes === 'string' ? String(snap.notes) : ''
    );
    setShowEdit(true);
  }

  const selectedContract: ContractRow | null =
    contracts.find((c) => c.id === selectedContractId) ?? null;
  const detailContract: ContractFull | null =
    detail?.contract ?? (detail?.id ? (detail as unknown as ContractFull) : null);
  const hasDraft = contracts.some((c) => c.status === 'draft');
  const isDraft = selectedContract?.status === 'draft';
  const flowIndex = selectedContract ? statusToFlowIndex(String(selectedContract.status)) : -1;

  const snapshot: SnapshotShape | null = (detailContract?.snapshot ?? null) as SnapshotShape | null;
  const financials: Record<string, unknown> = useMemo(
    () => ((snapshot?.financials ?? {}) as Record<string, unknown>),
    [snapshot]
  );
  const termsCount = Array.isArray(snapshot?.terms)
    ? (snapshot?.terms as unknown[]).length
    : snapshot?.terms && typeof snapshot.terms === 'object'
      ? Object.keys(snapshot.terms).length
      : 0;

  async function afterMutation(contractId?: string) {
    if (selectedBookingId) await refreshList(selectedBookingId);
    await fetchDetail(contractId ?? selectedContractId);
  }

  async function handleGenerate() {
    if (!selectedBookingId) return;
    setBusy('generate');
    try {
      const created = await api<ContractRow>(`/api/bookings/${selectedBookingId}/contracts`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      await refreshList(selectedBookingId);
      if (created?.id) setSelectedContractId(created.id);
    } catch (err) {
      surfaceError(err, notify, 'Génération impossible');
    } finally {
      setBusy(null);
    }
  }

  async function handleSend() {
    if (!selectedContractId) return;
    setBusy('send');
    try {
      const res = await api<SendResult>(`/api/contracts/${selectedContractId}/send`, {
        method: 'POST',
        body: JSON.stringify({
          phone: sendPhone || undefined,
          expiresHours: Number(expiresHours),
        }),
      });
      setSendResult(res);
      await afterMutation();
    } catch (err) {
      surfaceError(err, notify, 'Envoi impossible');
    } finally {
      setBusy(null);
    }
  }

  async function handleEditDraft() {
    if (!selectedContractId) return;
    setBusy('edit');
    try {
      await api(`/api/contracts/${selectedContractId}`, {
        method: 'PATCH',
        // API schema uses avanceAmount/cautionAmount (unknown keys are stripped).
        body: JSON.stringify({
          totalPrice: editTotal === '' ? undefined : Number(editTotal),
          avanceAmount: editAvance === '' ? undefined : Number(editAvance),
          cautionAmount: editCaution === '' ? undefined : Number(editCaution),
          notes: editNotes || undefined,
        }),
      });
      setShowEdit(false);
      await afterMutation();
    } catch (err) {
      surfaceError(err, notify, 'Modification impossible');
    } finally {
      setBusy(null);
    }
  }

  async function handleCancel() {
    if (!selectedContractId) return;
    setBusy('cancel');
    try {
      await api(`/api/contracts/${selectedContractId}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason: cancelReason }),
      });
      setShowCancel(false);
      setCancelReason('');
      await afterMutation();
    } catch (err) {
      surfaceError(err, notify, 'Annulation impossible');
    } finally {
      setBusy(null);
    }
  }

  async function handleSupersede() {
    if (!selectedContractId) return;
    if (!confirm('Remplacer ce contrat par une nouvelle version ? L’actuelle sera marquée remplacée.')) return;
    setBusy('supersede');
    try {
      const created = await api<ContractRow>(`/api/contracts/${selectedContractId}/supersede`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      await refreshList(selectedBookingId);
      if (created?.id) {
        setSelectedContractId(created.id);
        await fetchDetail(created.id);
      } else {
        await afterMutation();
      }
    } catch (err) {
      surfaceError(err, notify, 'Remplacement impossible');
    } finally {
      setBusy(null);
    }
  }

  async function handleCountersign() {
    if (!selectedContractId) return;
    setBusy('countersign');
    try {
      await api(`/api/contracts/${selectedContractId}/countersign`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      await afterMutation();
    } catch (err) {
      surfaceError(err, notify, 'Contresignature impossible');
    } finally {
      setBusy(null);
    }
  }

  async function handleDownload() {
    if (!selectedContractId) return;
    setBusy('download');
    try {
      // Spec endpoint GET /api/contracts/:id/download (may not exist yet in the
      // parallel API stream) — fetch+blob so a 404/409 surfaces as an alert
      // instead of navigating away to a JSON error page.
      const res = await fetch(`/api/contracts/${selectedContractId}/download`);
      if (!res.ok) {
        let msg = `Erreur ${res.status}`;
        try {
          const j = (await res.json()) as { error?: unknown };
          if (typeof j?.error === 'string' && j.error) msg = j.error;
        } catch {
          /* keep default message */
        }
        notify(`Téléchargement impossible : ${msg}`, 'error');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Contrat-${String(selectedContract?.number ?? selectedContractId)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      notify(`Téléchargement impossible : ${err instanceof Error ? err.message : 'Erreur inconnue'}`, 'error');
    } finally {
      setBusy(null);
    }
  }

  function copyLink() {
    if (!sendResult?.link) return;
    void navigator.clipboard.writeText(sendResult.link).then(
      () => notify('Lien copié.'),
      () => notify('Copie impossible — sélectionnez le lien manuellement.', 'error')
    );
  }

  const btnPrimary =
    'inline-flex items-center gap-1.5 px-3 py-2 rounded-2xl bg-ink text-shell text-xs font-bold hover:brightness-110 disabled:opacity-50 transition-colors cursor-pointer';
  const btnAccent =
    'inline-flex items-center gap-1.5 px-3 py-2 rounded-2xl bg-[#FF8A00] text-slate-950 text-xs font-bold hover:bg-[#ea7900] disabled:opacity-50 transition-colors cursor-pointer';
  const btnGhost =
    'inline-flex items-center gap-1.5 px-3 py-2 rounded-2xl bg-card border border-line text-ink text-xs font-bold hover:bg-shell disabled:opacity-50 transition-colors cursor-pointer';
  const inputCls =
    'w-full px-3 py-2 bg-card border border-line rounded-2xl text-xs text-ink placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand';

  return (
    <div className="bg-card border border-line rounded-2xl p-4 sm:p-5 shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-8 h-8 rounded-2xl bg-ink flex items-center justify-center text-[#FF8A00]">
          <ClipboardList className="w-4 h-4" />
        </div>
        <div>
          <h3 className="text-xs font-extrabold text-ink uppercase tracking-wider">
            Contrats électroniques (e-contracts)
          </h3>
          <p className="text-xs text-muted">Générez, envoyez et faites signer le contrat de location.</p>
        </div>
      </div>

      {/* Booking picker */}
      <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
        <label className="block">
          <span className="text-xs font-bold text-muted">Réservation</span>
          <select
            value={selectedBookingId}
            onChange={(e) => handleSelectBooking(e.target.value)}
            className="mt-1 w-full px-3 py-2 bg-card border border-line rounded-2xl text-xs text-ink focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
          >
            <option value="">— Choisir une réservation —</option>
            {openBookings.map((b) => (
              <option key={b.id} value={b.id}>
                {b.customerName} — {b.vehicleName} — {b.pickupDate} → {b.dropoffDate} ({b.status})
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => selectedBookingId && void refreshList(selectedBookingId)}
          disabled={!selectedBookingId || loadingList}
          className={btnGhost}
          title="Rafraîchir la liste"
        >
          {loadingList ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Actualiser
        </button>
      </div>

      {!selectedBookingId && (
        <p className="mt-3 text-xs text-muted">
          Sélectionnez une réservation ouverte (pending, confirmed, active) pour gérer ses contrats.
        </p>
      )}

      {selectedBookingId && (
        <>
          {loadingList && (
            <p className="mt-3 text-xs text-muted inline-flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement des contrats…
            </p>
          )}
          {listError && (
            <p className="mt-3 text-xs font-semibold text-danger inline-flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> {listError}
            </p>
          )}

          {!loadingList && !listError && contracts.length === 0 && (
            <div className="mt-3 p-4 rounded-2xl bg-shell border border-line text-center">
              <p className="text-xs text-muted font-semibold">Aucun contrat pour cette réservation.</p>
              <button type="button" onClick={() => void handleGenerate()} disabled={busy === 'generate'} className={`${btnAccent} mt-2`}>
                {busy === 'generate' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FilePlus2 className="w-3.5 h-3.5" />}
                Générer le contrat
              </button>
            </div>
          )}

          {contracts.length > 0 && (
            <div className="mt-3 grid gap-3 lg:grid-cols-[280px_1fr]">
              {/* Timeline of versions */}
              <div className="rounded-2xl border border-line overflow-hidden">
                <div className="px-3 py-2 bg-shell border-b border-line text-xs font-extrabold text-ink uppercase tracking-wider">
                  Versions ({contracts.length})
                </div>
                <ul className="divide-y divide-line">
                  {contracts.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => handleSelectContract(c.id)}
                        className={`w-full text-left px-3 py-2.5 transition-colors cursor-pointer ${
                          c.id === selectedContractId ? 'bg-warning/10' : 'hover:bg-shell'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-extrabold text-ink">
                            {str(c.number)} <span className="font-semibold text-muted">· v{c.version}</span>
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${
                              STATUS_CHIP[String(c.status)] ?? STATUS_CHIP.draft
                            }`}
                          >
                            {STATUS_LABEL[String(c.status)] ?? String(c.status)}
                          </span>
                        </div>
                        <div className="mt-0.5 text-[11px] text-muted">Créé le {fmtDate(c.createdAt)}</div>
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="p-2.5 border-t border-line">
                  <button
                    type="button"
                    onClick={() => void handleGenerate()}
                    disabled={busy === 'generate' || hasDraft}
                    title={hasDraft ? 'Un brouillon existe déjà — modifiez-le ou remplacez la version signée.' : 'Générer un contrat'}
                    className={`${btnGhost} w-full justify-center`}
                  >
                    {busy === 'generate' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FilePlus2 className="w-3.5 h-3.5" />}
                    Générer
                  </button>
                  {hasDraft && (
                    <p className="mt-1.5 text-[11px] text-muted text-center">Un brouillon existe déjà.</p>
                  )}
                </div>
              </div>

              {/* Selected contract */}
              <div>
                {selectedContract && (
                  <div className="rounded-2xl border border-line p-3">
                    {/* Status flow hint bar */}
                    <div className="flex items-center gap-1 mb-3" aria-label="Progression du contrat">
                      {FLOW_STEPS.map((label, i) => (
                        <div key={label} className="flex-1 flex items-center gap-1 last:flex-none">
                          <div
                            className={`flex-1 px-2 py-1 rounded-full text-center text-[10px] font-bold whitespace-nowrap ${
                              flowIndex >= 0 && i <= flowIndex
                                ? 'bg-ink text-shell'
                                : 'bg-shell text-muted'
                            }`}
                          >
                            {label}
                          </div>
                          {i < FLOW_STEPS.length - 1 && <div className="w-2 h-px bg-line shrink-0" />}
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-extrabold text-ink">
                        {str(selectedContract.number)} · v{selectedContract.version}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${
                          STATUS_CHIP[String(selectedContract.status)] ?? STATUS_CHIP.draft
                        }`}
                      >
                        {STATUS_LABEL[String(selectedContract.status)] ?? String(selectedContract.status)}
                      </span>
                      {loadingDetail && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted" />}
                    </div>

                    {/* Actions */}
                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        className={btnGhost}
                        onClick={() => {
                          setShowPreview(true);
                          if (selectedContractId) void fetchDetail(selectedContractId);
                        }}
                      >
                        <Eye className="w-3.5 h-3.5" /> Aperçu
                      </button>
                      <button type="button" className={btnAccent} onClick={openSend}>
                        <Send className="w-3.5 h-3.5" /> Envoyer
                      </button>
                      {isDraft && (
                        <button type="button" className={btnGhost} onClick={openEdit}>
                          <PenLine className="w-3.5 h-3.5" /> Modifier draft
                        </button>
                      )}
                      <button
                        type="button"
                        className={btnGhost}
                        onClick={() => void handleDownload()}
                        disabled={busy === 'download' || selectedContract.status !== 'signed'}
                        title={
                          selectedContract.status === 'signed'
                            ? 'Télécharger le PDF signé'
                            : 'Le PDF sera disponible après la signature du client'
                        }
                      >
                        {busy === 'download' ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Download className="w-3.5 h-3.5" />
                        )}
                        Télécharger PDF
                      </button>
                      <button
                        type="button"
                        className={btnPrimary}
                        onClick={() => void handleCountersign()}
                        disabled={busy === 'countersign' || selectedContract.status !== 'signed'}
                        title={
                          selectedContract.status === 'signed'
                            ? 'Ajouter la contresignature de l’agence'
                            : 'La signature du client est requise avant la contresignature'
                        }
                      >
                        {busy === 'countersign' ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <ShieldCheck className="w-3.5 h-3.5" />
                        )}
                        Contresigner agence
                      </button>
                      <button type="button" className={btnGhost} onClick={() => void handleSupersede()} disabled={busy === 'supersede'}>
                        {busy === 'supersede' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
                        Remplacer
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-2xl bg-card border border-danger/30 text-danger text-xs font-bold hover:bg-danger/10 disabled:opacity-50 transition-colors cursor-pointer"
                        onClick={() => setShowCancel(true)}
                      >
                        <XCircle className="w-3.5 h-3.5" /> Annuler
                      </button>
                      <button
                        type="button"
                        className={btnGhost}
                        onClick={() => {
                          setShowAudit((v) => !v);
                          if (selectedContractId) void fetchDetail(selectedContractId);
                        }}
                      >
                        <History className="w-3.5 h-3.5" /> Historique audit
                      </button>
                    </div>

                    {selectedContract.status === 'signed' ? (
                      <p className="mt-2.5 flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Contrat signé : le PDF complet est téléchargeable et une copie est envoyée à l’agence.
                      </p>
                    ) : (
                      <p className="mt-2.5 text-[11px] text-muted">
                        Le PDF signé (conditions, coordonnées de l’agence et signatures) sera disponible dès la signature du client.
                      </p>
                    )}

                    {/* Audit expandable */}
                    {showAudit && (
                      <div className="mt-3 rounded-2xl bg-shell border border-line p-3">
                        <p className="text-xs font-extrabold text-ink uppercase tracking-wider mb-2">
                          Journal d’audit
                        </p>
                        {(() => {
                          const audits = detail?.audits ?? [];
                          if (audits.length === 0)
                            return <p className="text-xs text-muted">Aucun événement pour le moment.</p>;
                          return (
                            <ul className="space-y-1.5 max-h-44 overflow-y-auto">
                              {audits.map((a, i) => (
                                <li key={String(a.id ?? i)} className="text-xs text-muted flex flex-wrap gap-x-2">
                                  <span className="font-bold text-ink">{str(a.event ?? a.action)}</span>
                                  <span className="text-muted">· {str(a.actor ?? a.actorRole)}</span>
                                  <span className="text-muted ml-auto">{fmtDate(str(a.createdAt ?? a.date) === '—' ? undefined : String(a.createdAt ?? a.date))}</span>
                                </li>
                              ))}
                            </ul>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Separation note */}
      <div className="mt-3 p-3 rounded-2xl bg-warning/10 border border-warning/30 flex gap-2">
        <AlertTriangle className="w-4 h-4 text-[#FF8A00] shrink-0 mt-0.5" />
        <p className="text-xs text-muted">
          <span className="font-bold">État des lieux</span> (photos, kilométrage, carburant) signé séparément au
          pickup — voir Handovers ci-dessous.
        </p>
      </div>

      {/* ---- Preview modal ---- */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/55" role="dialog" aria-modal="true">
          <div className="w-full max-w-lg bg-card rounded-2xl p-4 sm:p-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-extrabold text-ink uppercase tracking-wider">
                Aperçu — {detailContract ? str(detailContract.number) : '…'} (v{detailContract?.version ?? '…'})
              </h4>
              <button type="button" onClick={() => setShowPreview(false)} className={btnGhost} aria-label="Fermer">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            {loadingDetail && (
              <p className="text-xs text-muted inline-flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement…
              </p>
            )}
            {detailContract && (
              <div className="space-y-3 text-xs">
                <div className="grid sm:grid-cols-2 gap-2">
                  <div className="p-2.5 rounded-2xl bg-shell border border-line">
                    <p className="font-extrabold text-ink uppercase tracking-wider text-[10px] mb-1">Parties</p>
                    <pre className="whitespace-pre-wrap text-muted text-[11px]">
                      {JSON.stringify(snapshot?.parties ?? { booking: selectedBooking?.customerName }, null, 2)}
                    </pre>
                  </div>
                  <div className="p-2.5 rounded-2xl bg-shell border border-line">
                    <p className="font-extrabold text-ink uppercase tracking-wider text-[10px] mb-1">Véhicule</p>
                    <pre className="whitespace-pre-wrap text-muted text-[11px]">
                      {JSON.stringify(snapshot?.vehicle ?? { vehicle: selectedBooking?.vehicleName }, null, 2)}
                    </pre>
                  </div>
                </div>
                <div className="p-2.5 rounded-2xl bg-shell border border-line">
                  <p className="font-extrabold text-ink uppercase tracking-wider text-[10px] mb-1">Dates</p>
                  <pre className="whitespace-pre-wrap text-muted text-[11px]">
                    {JSON.stringify(
                      snapshot?.dates ?? {
                        pickup: selectedBooking?.pickupDate,
                        dropoff: selectedBooking?.dropoffDate,
                      },
                      null,
                      2
                    )}
                  </pre>
                </div>
                <div className="p-2.5 rounded-2xl bg-shell border border-line">
                  <p className="font-extrabold text-ink uppercase tracking-wider text-[10px] mb-1">
                    Financier
                  </p>
                  <table className="w-full text-[11px]">
                    <tbody>
                      {Object.entries({
                        ...financials,
                        ...(detailContract.totalPrice != null ? { totalPrice: detailContract.totalPrice } : {}),
                      }).map(([k, v]) => (
                        <tr key={k} className="border-t border-line first:border-0">
                          <td className="py-1 pr-2 font-semibold text-muted">{k}</td>
                          <td className="py-1 text-right text-ink">
                            {typeof v === 'number' ? fmtMoney(v) : str(v)}
                          </td>
                        </tr>
                      ))}
                      {Object.keys(financials).length === 0 && detailContract.totalPrice == null && (
                        <tr>
                          <td className="py-1 text-muted">Aucune donnée financière.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="text-muted">
                  Conditions générales : <span className="font-bold text-ink">{termsCount}</span> clause(s).
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---- Send modal ---- */}
      {showSend && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/55" role="dialog" aria-modal="true">
          <div className="w-full max-w-md bg-card rounded-2xl p-4 sm:p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-extrabold text-ink uppercase tracking-wider">Envoyer au client</h4>
              <button type="button" onClick={() => { setShowSend(false); setSendResult(null); }} className={btnGhost} aria-label="Fermer">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid gap-2">
              <label className="block">
                <span className="text-xs font-bold text-muted">Téléphone</span>
                <input value={sendPhone} onChange={(e) => setSendPhone(e.target.value)} placeholder="+212…" className={`${inputCls} mt-1`} />
              </label>
              <label className="block">
                <span className="text-xs font-bold text-muted">Expiration</span>
                <select value={expiresHours} onChange={(e) => setExpiresHours(e.target.value)} className={`${inputCls} mt-1`}>
                  <option value="24">24 heures</option>
                  <option value="48">48 heures</option>
                  <option value="72">72 heures</option>
                  <option value="168">7 jours</option>
                </select>
              </label>
              <button type="button" onClick={() => void handleSend()} disabled={busy === 'send'} className={`${btnAccent} justify-center`}>
                {busy === 'send' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Envoyer le lien
              </button>
              {sendResult && (
                <div className="p-3 rounded-2xl bg-success/10 border border-success/30 space-y-2">
                  <p className="text-xs text-success inline-flex items-center gap-1.5 font-bold">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Envoyé → {sendResult.maskedPhone} · expire {fmtDate(sendResult.expiresAt)}
                  </p>
                  <div className="flex gap-1.5">
                    <input readOnly value={sendResult.link} className={`${inputCls} bg-card`} onFocus={(e) => e.target.select()} />
                    <button type="button" onClick={copyLink} className={btnGhost} title="Copier">
                      <Copy className="w-3.5 h-3.5" /> Copier
                    </button>
                  </div>
                  <a href={sendResult.waLink} target="_blank" rel="noreferrer" className={`${btnGhost} w-full justify-center`}>
                    <MessageCircle className="w-3.5 h-3.5" /> Ouvrir WhatsApp
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ---- Edit draft modal ---- */}
      {showEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/55" role="dialog" aria-modal="true">
          <div className="w-full max-w-md bg-card rounded-2xl p-4 sm:p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-extrabold text-ink uppercase tracking-wider">Modifier le brouillon</h4>
              <button type="button" onClick={() => setShowEdit(false)} className={btnGhost} aria-label="Fermer">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid gap-2">
              <label className="block">
                <span className="text-xs font-bold text-muted">Prix total (MAD)</span>
                <input value={editTotal} onChange={(e) => setEditTotal(e.target.value)} inputMode="decimal" className={`${inputCls} mt-1`} />
              </label>
              <label className="block">
                <span className="text-xs font-bold text-muted">Avance (MAD)</span>
                <input value={editAvance} onChange={(e) => setEditAvance(e.target.value)} inputMode="decimal" className={`${inputCls} mt-1`} />
              </label>
              <label className="block">
                <span className="text-xs font-bold text-muted">Caution (MAD)</span>
                <input value={editCaution} onChange={(e) => setEditCaution(e.target.value)} inputMode="decimal" className={`${inputCls} mt-1`} />
              </label>
              <label className="block">
                <span className="text-xs font-bold text-muted">Notes</span>
                <textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={3} className={`${inputCls} mt-1`} />
              </label>
              <button type="button" onClick={() => void handleEditDraft()} disabled={busy === 'edit'} className={`${btnPrimary} justify-center`}>
                {busy === 'edit' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PenLine className="w-3.5 h-3.5" />}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Cancel modal ---- */}
      {showCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/55" role="dialog" aria-modal="true">
          <div className="w-full max-w-md bg-card rounded-2xl p-4 sm:p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-extrabold text-ink uppercase tracking-wider">Annuler le contrat</h4>
              <button type="button" onClick={() => setShowCancel(false)} className={btnGhost} aria-label="Fermer">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <label className="block">
              <span className="text-xs font-bold text-muted">Motif</span>
              <textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={3} placeholder="Raison de l’annulation…" className={`${inputCls} mt-1`} />
            </label>
            <button
              type="button"
              onClick={() => void handleCancel()}
              disabled={busy === 'cancel' || !cancelReason.trim()}
              className="mt-2 inline-flex w-full items-center justify-center gap-1.5 px-3 py-2 rounded-2xl bg-danger text-white text-xs font-bold hover:brightness-110 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {busy === 'cancel' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
              Confirmer l’annulation
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
